import React from "react";
import { Link } from "react-router-dom";
import { Bed, Bath, Maximize, MapPin, BadgeCheck, Sparkles, Flame } from "lucide-react";
import { formatPrice, formatPropertyPrice, isShortStayContract } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { computeMatchScore, readUserPrefs } from "@/lib/matchScore";
import MatchBadge from "@/components/MatchBadge";
import { propertyPath } from "@/lib/seo";

const NEW_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;  // 3 days

function isRecentlyPublished(createdAt) {
    if (!createdAt) return false;
    const t = Date.parse(createdAt);
    if (Number.isNaN(t)) return false;
    return (Date.now() - t) < NEW_WINDOW_MS;
}

export default function PropertyCard({ property, index = 0, stats }) {
    const { user } = useAuth();
    const cover = property.photos?.[0] || "https://images.unsplash.com/photo-1545597390-c92ae038a5a8?auto=format&fit=crop&w=1200&q=80";
    const isRent = property.transaction_type === "affitto";
    const isShortStay = isShortStayContract(property) && !!property.price_period;
    const isNew = isRecentlyPublished(property.created_at);
    const matchScore = computeMatchScore(readUserPrefs(user), property);
    const viewsToday = stats?.views_24h || 0;

    // Location line: mostra "Città, indirizzo" se sbloccato, altrimenti solo "Città · Regione"
    const locationText = property.locked
        ? `${property.city}${property.zone ? ` · ${property.zone}` : ""}`
        : property.address
            ? `${property.city}, ${property.address}`
            : `${property.city}${property.zone ? ` · ${property.zone}` : property.region ? ` · ${property.region}` : ""}`;

    return (
        <Link
            to={propertyPath(property)}
            data-testid={`property-card-${property.id}`}
            className={`group block h-full fade-up stagger-${(index % 4) + 1}`}
        >
            <article className="flex flex-col h-full">
                {/* --- IMAGE — clean, no text overlay --- */}
                <div className="relative aspect-video overflow-hidden bg-ink-900 rounded-2xl">
                    <img
                        src={cover}
                        alt={`${property.title} - ${property.city}`}
                        loading={index < 3 ? "eager" : "lazy"}
                        decoding="async"
                        className="w-full h-full object-cover object-center transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />

                    {/* Top-left stack: IN EVIDENZA + NUOVO (piccoli, ordinati) */}
                    <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
                        {property.is_featured && (
                            <span
                                data-testid={`badge-featured-${property.id}`}
                                className="bg-gold text-black px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase font-bold shadow-md rounded-sm"
                            >
                                In evidenza
                            </span>
                        )}
                        {isNew && (
                            <span
                                data-testid={`badge-new-${property.id}`}
                                className="inline-flex items-center gap-1 bg-emerald-500 text-white px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase font-bold shadow-md rounded-sm ring-1 ring-emerald-500/40 animate-pulse"
                                title="Pubblicato negli ultimi 3 giorni"
                            >
                                <Sparkles size={9} /> Nuovo
                            </span>
                        )}
                    </div>

                    {/* Top-right stack: Affitto tag + Match Badge */}
                    <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
                        {isRent && (
                            <span
                                data-testid={`badge-rent-${property.id}`}
                                className="bg-ink-950/85 backdrop-blur text-gold border border-gold/50 px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase font-bold rounded-sm"
                            >
                                Affitto
                            </span>
                        )}
                        {matchScore != null && (
                            <div data-testid={`match-badge-card-${property.id}`}>
                                <MatchBadge score={matchScore} size="sm" />
                            </div>
                        )}
                    </div>

                    {/* Bottom-left: fire counter con sfondo scuro semi-trasparente */}
                    {viewsToday > 0 && (
                        <div
                            data-testid={`fire-views-${property.id}`}
                            className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-black/70 backdrop-blur-md text-white px-2.5 py-1 rounded-full text-[11px] font-medium border border-white/20 shadow-lg fire-pulse"
                            title="Interesse crescente su questo annuncio"
                        >
                            <Flame size={12} className="text-orange-400 fire-flicker" />
                            <span><b>{viewsToday}</b> visualizzazioni recenti</span>
                        </div>
                    )}
                </div>

                {/* --- TEXT SECTION below image --- */}
                <div className="pt-4 flex-1 flex flex-col">
                    <h3
                        data-testid={`card-title-${property.id}`}
                        className="font-serif font-bold text-lg text-ink-50 leading-tight line-clamp-2 mb-2"
                    >
                        {property.title}
                    </h3>
                    <div className="flex items-center gap-1.5 text-xs text-ink-400 mb-4">
                        <MapPin size={12} className="text-gold shrink-0" />
                        <span className="truncate">{locationText}</span>
                    </div>

                    {/* --- Footer: dati tecnici + prezzo (mt-auto per allineamento uniforme in griglia) --- */}
                    <div className="mt-auto pt-3 flex items-end justify-between gap-3 border-t border-white/5 group-hover:border-gold/40 transition-colors duration-500">
                        <div className="flex items-center gap-4 text-ink-400 text-sm">
                            <span className="flex items-center gap-1.5"><Bed size={14} /> {property.rooms}</span>
                            <span className="flex items-center gap-1.5"><Bath size={14} /> {property.bathrooms}</span>
                            <span className="flex items-center gap-1.5"><Maximize size={14} /> {property.surface_sqm} m²</span>
                        </div>
                        <div className="font-serif text-xl text-price whitespace-nowrap">
                            {isShortStay
                                ? formatPropertyPrice(property)
                                : <>{formatPrice(property.price)}{isRent && <span className="text-xs text-ink-400 font-sans ml-1">/mese</span>}</>}
                        </div>
                    </div>

                    {property.documentation_verified && (
                        <div data-testid={`badge-docs-${property.id}`} className="mt-2 flex items-center gap-1.5 text-[11px] text-green-400">
                            <BadgeCheck size={12} /> Documentazione verificata
                        </div>
                    )}
                </div>
            </article>
        </Link>
    );
}
